/**
 * NavigatorX Support Dashboard
 *
 * Comprehensive customer support management:
 * - Ticket queue with filtering and assignment
 * - User feedback viewer
 * - Feature requests with voting
 * - Bug reports tracking
 * - Macro management (create, execute, delete)
 * - Internal notes on tickets
 * - Support metrics overview
 *
 * @author Claude AI for TGE.LA
 * @version 2.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  Ticket,
  MessageSquare,
  Lightbulb,
  Bug,
  Zap,
  Search,
  RefreshCw,
  Loader2,
  Clock,
  CheckCircle,
  User,
  Star,
  ThumbsUp,
  Send,
  Eye,
  Plus,
  Trash2,
  Lock,
  UserCheck,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { Timestamp } from 'firebase/firestore';
import { auth } from '../../../utils/firebase/client';
import {
  getTickets,
  getTicket,
  changeTicketStatus,
  assignTicket,
  addTicketMessage,
  updateTicket,
  getFeedback,
  reviewFeedback,
  getFeatureRequests,
  updateFeatureRequestStatus,
  getBugReports,
  updateBugReportStatus,
  getMacros,
  createMacro,
  executeMacro,
  getSupportMetrics,
  lookupUserForSupport,
  type SupportTicket,
  type UserFeedback,
  type FeatureRequest,
  type BugReport,
  type SupportMacro,
  type MacroAction,
  type SupportMetrics
} from '../../../utils/services/navigatorXSupportService';
import type { NavUser } from '../../../types/navigatorx';

// ============================================================================
// MODULE-LEVEL PURE FUNCTIONS
// ============================================================================

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred';
}

function formatTimestamp(ts: unknown): string {
  if (!ts) return 'N/A';
  if (ts instanceof Timestamp) return ts.toDate().toLocaleDateString() + ' ' + ts.toDate().toLocaleTimeString();
  if (ts instanceof Date) return ts.toLocaleDateString() + ' ' + ts.toLocaleTimeString();
  if (typeof ts === 'string') return new Date(ts).toLocaleDateString() + ' ' + new Date(ts).toLocaleTimeString();
  if (typeof ts === 'number') return new Date(ts).toLocaleDateString() + ' ' + new Date(ts).toLocaleTimeString();
  return 'N/A';
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / 1440)}d`;
}

function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'urgent': return 'bg-red-100 text-red-700';
    case 'high': return 'bg-orange-100 text-orange-700';
    case 'medium': return 'bg-yellow-100 text-yellow-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'open': return 'bg-blue-100 text-blue-700';
    case 'in_progress': return 'bg-purple-100 text-purple-700';
    case 'waiting_user': return 'bg-yellow-100 text-yellow-700';
    case 'resolved': return 'bg-green-100 text-green-700';
    case 'closed': return 'bg-gray-100 text-gray-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

function getBugSeverityColor(severity: string): string {
  switch (severity) {
    case 'blocker': return 'bg-red-600 text-white';
    case 'critical': return 'bg-red-100 text-red-700';
    case 'major': return 'bg-orange-100 text-orange-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

function getFeedbackStatusColor(status: string): string {
  switch (status) {
    case 'new': return 'bg-blue-100 text-blue-700';
    case 'actioned': return 'bg-green-100 text-green-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface SupportDashboardProps {
  embedded?: boolean;
}

interface UserLookupResult {
  user: NavUser;
  tickets: SupportTicket[];
  feedback: UserFeedback[];
}

type ActiveTab = 'tickets' | 'feedback' | 'features' | 'bugs' | 'macros' | 'lookup';

const TICKET_STATUSES: SupportTicket['status'][] = ['open', 'in_progress', 'waiting_user', 'resolved', 'closed'];
const TICKET_PRIORITIES: SupportTicket['priority'][] = ['low', 'medium', 'high', 'urgent'];
const FEATURE_STATUSES: FeatureRequest['status'][] = ['submitted', 'under_review', 'planned', 'in_progress', 'completed', 'declined'];
const BUG_STATUSES: BugReport['status'][] = ['open', 'confirmed', 'in_progress', 'fixed', 'cannot_reproduce', 'wont_fix'];

// ============================================================================
// COMPONENT
// ============================================================================

export default function SupportDashboard({ embedded = false }: SupportDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ActiveTab>('tickets');

  const [metrics, setMetrics] = useState<SupportMetrics | null>(null);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [feedback, setFeedback] = useState<UserFeedback[]>([]);
  const [featureRequests, setFeatureRequests] = useState<FeatureRequest[]>([]);
  const [bugReports, setBugReports] = useState<BugReport[]>([]);
  const [macros, setMacros] = useState<SupportMacro[]>([]);

  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [lookupUserId, setLookupUserId] = useState('');
  const [lookupResult, setLookupResult] = useState<UserLookupResult | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [showCreateMacro, setShowCreateMacro] = useState(false);
  const [newMacroName, setNewMacroName] = useState('');
  const [newMacroDescription, setNewMacroDescription] = useState('');
  const [newMacroCategory, setNewMacroCategory] = useState('general');
  const [newMacroActionType, setNewMacroActionType] = useState<MacroAction['type']>('send_message');
  const [newMacroActionParam, setNewMacroActionParam] = useState('');
  const [newMacroActions, setNewMacroActions] = useState<MacroAction[]>([]);

  const adminId = auth.currentUser?.uid || 'unknown';

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [m, t, f, fr, br, mac] = await Promise.all([
        getSupportMetrics(),
        getTickets(),
        getFeedback(),
        getFeatureRequests(),
        getBugReports(),
        getMacros()
      ]);

      setMetrics(m);
      setTickets(t);
      setFeedback(f);
      setFeatureRequests(fr);
      setBugReports(br);
      setMacros(mac);
    } catch (error) {
      console.error('Error loading support data:', error);
      toast.error('Failed to load support data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (ticketId: string, status: SupportTicket['status']) => {
    try {
      await changeTicketStatus(ticketId, status, adminId);
      toast.success('Status updated');
      loadAllData();
      if (selectedTicket?.id === ticketId) {
        const updated = await getTicket(ticketId);
        setSelectedTicket(updated);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleAssignTicket = async (ticketId: string) => {
    try {
      await assignTicket(ticketId, adminId, adminId);
      toast.success('Ticket assigned to you');
      loadAllData();
      if (selectedTicket?.id === ticketId) {
        const updated = await getTicket(ticketId);
        setSelectedTicket(updated);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSendReply = async () => {
    if (!selectedTicket?.id || !replyMessage.trim()) return;

    try {
      await addTicketMessage(selectedTicket.id, {
        sender: 'agent',
        senderId: adminId,
        content: replyMessage,
        isInternal: false
      }, adminId);

      toast.success('Reply sent');
      setReplyMessage('');
      const updated = await getTicket(selectedTicket.id);
      setSelectedTicket(updated);
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleAddInternalNote = async () => {
    if (!selectedTicket?.id || !internalNote.trim()) return;

    try {
      await updateTicket(selectedTicket.id, {
        internalNotes: [...(selectedTicket.internalNotes || []), `[${new Date().toISOString()}] ${internalNote}`]
      }, adminId);

      toast.success('Internal note added');
      setInternalNote('');
      const updated = await getTicket(selectedTicket.id);
      setSelectedTicket(updated);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleReviewFeedback = async (feedbackId: string, status: UserFeedback['status']) => {
    try {
      await reviewFeedback(feedbackId, adminId, undefined, status);
      toast.success('Feedback reviewed');
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleFeatureStatus = async (id: string, status: FeatureRequest['status']) => {
    try {
      await updateFeatureRequestStatus(id, status, adminId);
      toast.success('Status updated');
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleBugStatus = async (id: string, status: BugReport['status']) => {
    try {
      await updateBugReportStatus(id, status, adminId);
      toast.success('Status updated');
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUserLookup = async () => {
    if (!lookupUserId.trim()) return;

    try {
      const result = await lookupUserForSupport(lookupUserId.trim());
      if (result) {
        setLookupResult(result as UserLookupResult);
      } else {
        setLookupResult(null);
        toast.error('User not found');
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleExecuteMacro = async (macroId: string) => {
    if (!selectedTicket?.id) return;

    try {
      await executeMacro(macroId, selectedTicket.id, adminId);
      toast.success('Macro executed');
      const updated = await getTicket(selectedTicket.id);
      setSelectedTicket(updated);
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleAddMacroAction = () => {
    if (!newMacroActionParam.trim()) return;

    let params: Record<string, string>;
    switch (newMacroActionType) {
      case 'send_message':
        params = { message: newMacroActionParam };
        break;
      case 'change_status':
        params = { status: newMacroActionParam };
        break;
      case 'add_tag':
        params = { tag: newMacroActionParam };
        break;
      case 'assign':
        params = { assignee: newMacroActionParam };
        break;
      default:
        params = { value: newMacroActionParam };
        break;
    }

    setNewMacroActions(prev => [...prev, { type: newMacroActionType, params }]);
    setNewMacroActionParam('');
  };

  const handleCreateMacro = async () => {
    if (!newMacroName.trim() || newMacroActions.length === 0) {
      toast.error('Macro needs a name and at least one action');
      return;
    }

    try {
      await createMacro({
        name: newMacroName,
        description: newMacroDescription,
        category: newMacroCategory,
        actions: newMacroActions,
        createdBy: adminId
      }, adminId);

      toast.success('Macro created');
      setShowCreateMacro(false);
      setNewMacroName('');
      setNewMacroDescription('');
      setNewMacroCategory('general');
      setNewMacroActions([]);
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const filteredTickets = tickets.filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Support Dashboard</h2>
            <p className="text-gray-500">Manage tickets, feedback, and customer requests</p>
          </div>
          <Button variant="outline" onClick={loadAllData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      )}

      {/* Metrics Overview */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className={metrics.openTickets > 10 ? 'border-red-300 bg-red-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Open Tickets</p>
                  <p className="text-2xl font-bold">{metrics.openTickets}</p>
                </div>
                <Ticket className="h-6 w-6 text-gray-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Avg Response</p>
                  <p className="text-2xl font-bold">{formatDuration(metrics.avgFirstResponseMinutes)}</p>
                </div>
                <Clock className="h-6 w-6 text-gray-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Avg Resolution</p>
                  <p className="text-2xl font-bold">{formatDuration(metrics.avgResolutionMinutes)}</p>
                </div>
                <CheckCircle className="h-6 w-6 text-gray-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Satisfaction</p>
                  <p className="text-2xl font-bold">{metrics.avgSatisfaction}/5</p>
                </div>
                <Star className="h-6 w-6 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Resolved Today</p>
                  <p className="text-2xl font-bold text-green-600">{metrics.resolvedToday}</p>
                </div>
                <CheckCircle className="h-6 w-6 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b overflow-x-auto">
        {[
          { id: 'tickets' as const, label: 'Tickets', icon: Ticket, count: tickets.filter(t => t.status === 'open').length },
          { id: 'feedback' as const, label: 'Feedback', icon: MessageSquare, count: feedback.filter(f => f.status === 'new').length },
          { id: 'features' as const, label: 'Feature Requests', icon: Lightbulb, count: featureRequests.filter(f => f.status === 'submitted').length },
          { id: 'bugs' as const, label: 'Bug Reports', icon: Bug, count: bugReports.filter(b => b.status === 'open').length },
          { id: 'macros' as const, label: 'Macros', icon: Settings, count: undefined },
          { id: 'lookup' as const, label: 'User Lookup', icon: Search, count: undefined }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tickets Tab */}
      {activeTab === 'tickets' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Ticket List */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Tickets</CardTitle>
                <div className="flex gap-2">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="text-xs border rounded px-2 py-1"
                  >
                    <option value="all">All Status</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="waiting_user">Waiting</option>
                    <option value="resolved">Resolved</option>
                  </select>
                  <select
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    className="text-xs border rounded px-2 py-1"
                  >
                    <option value="all">All Priority</option>
                    <option value="urgent">Urgent</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {filteredTickets.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No tickets found</p>
                ) : (
                  filteredTickets.map(ticket => (
                    <div
                      key={ticket.id}
                      onClick={() => setSelectedTicket(ticket)}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedTicket?.id === ticket.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm line-clamp-1">{ticket.subject}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {ticket.userEmail || ticket.userId.substring(0, 8)}...
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge className={getPriorityColor(ticket.priority)}>
                            {ticket.priority}
                          </Badge>
                          <Badge className={getStatusColor(ticket.status)}>
                            {ticket.status.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                        <span>{ticket.category}</span>
                        <span>•</span>
                        <span>{formatTimestamp(ticket.createdAt).split(' ')[0]}</span>
                        {ticket.messages.length > 0 && (
                          <>
                            <span>•</span>
                            <span>{ticket.messages.length} messages</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Ticket Detail */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {selectedTicket ? 'Ticket Details' : 'Select a Ticket'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedTicket ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium">{selectedTicket.subject}</h4>
                    <p className="text-sm text-gray-600 mt-1">{selectedTicket.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Status</p>
                      <select
                        value={selectedTicket.status}
                        onChange={(e) => handleStatusChange(selectedTicket.id!, e.target.value as SupportTicket['status'])}
                        className="border rounded px-2 py-1 w-full mt-1"
                      >
                        {TICKET_STATUSES.map(s => (
                          <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <p className="text-gray-500">Priority</p>
                      <Badge className={getPriorityColor(selectedTicket.priority)}>
                        {selectedTicket.priority}
                      </Badge>
                    </div>
                  </div>

                  {/* Assignment */}
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <span className="text-gray-500">Assigned to: </span>
                      <span className="font-medium">
                        {selectedTicket.assignedTo
                          ? (selectedTicket.assignedTo === adminId ? 'You' : selectedTicket.assignedTo.substring(0, 12) + '...')
                          : 'Unassigned'}
                      </span>
                    </div>
                    {selectedTicket.assignedTo !== adminId && selectedTicket.id && (
                      <Button size="sm" variant="outline" onClick={() => handleAssignTicket(selectedTicket.id!)}>
                        <UserCheck className="h-3 w-3 mr-1" />
                        Assign to Me
                      </Button>
                    )}
                  </div>

                  {/* Messages */}
                  <div>
                    <p className="text-sm font-medium mb-2">Conversation</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded p-2">
                      {selectedTicket.messages.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-4">No messages yet</p>
                      ) : (
                        selectedTicket.messages.map(msg => (
                          <div
                            key={msg.id}
                            className={`p-2 rounded text-sm ${
                              msg.isInternal
                                ? 'bg-amber-50 border border-amber-200'
                                : msg.sender === 'agent' ? 'bg-blue-50 ml-4' : 'bg-gray-100 mr-4'
                            }`}
                          >
                            {msg.isInternal && (
                              <div className="flex items-center gap-1 text-xs text-amber-600 mb-1">
                                <Lock className="h-3 w-3" />
                                Internal Note
                              </div>
                            )}
                            <p>{msg.content}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {msg.sender === 'agent' ? 'Agent' : 'User'} &bull; {formatTimestamp(msg.timestamp)}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Reply */}
                  <div className="flex gap-2">
                    <Input
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply..."
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSendReply(); }}
                    />
                    <Button onClick={handleSendReply} disabled={!replyMessage.trim()}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Internal Notes */}
                  <div>
                    <p className="text-sm font-medium mb-2 flex items-center gap-1">
                      <Lock className="h-3 w-3 text-amber-500" />
                      Internal Notes ({selectedTicket.internalNotes?.length || 0})
                    </p>
                    {selectedTicket.internalNotes && selectedTicket.internalNotes.length > 0 && (
                      <div className="space-y-1 max-h-24 overflow-y-auto mb-2">
                        {selectedTicket.internalNotes.map((note, i) => (
                          <p key={i} className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                            {note}
                          </p>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input
                        value={internalNote}
                        onChange={(e) => setInternalNote(e.target.value)}
                        placeholder="Add internal note..."
                        className="text-sm"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddInternalNote(); }}
                      />
                      <Button size="sm" variant="outline" onClick={handleAddInternalNote} disabled={!internalNote.trim()}>
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  {macros.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-2">Quick Actions</p>
                      <div className="flex flex-wrap gap-2">
                        {macros.slice(0, 6).map(macro => (
                          <Button
                            key={macro.id}
                            size="sm"
                            variant="outline"
                            onClick={() => macro.id && handleExecuteMacro(macro.id)}
                          >
                            <Zap className="h-3 w-3 mr-1" />
                            {macro.name}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">
                  Select a ticket from the list to view details
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Feedback Tab */}
      {activeTab === 'feedback' && (
        <Card>
          <CardHeader>
            <CardTitle>User Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {feedback.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No feedback submitted</p>
              ) : (
                feedback.map(fb => (
                  <div key={fb.id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{fb.type}</Badge>
                          <Badge variant="outline">{fb.source}</Badge>
                          {fb.rating && (
                            <div className="flex items-center">
                              {[1, 2, 3, 4, 5].map(n => (
                                <Star
                                  key={n}
                                  className={`h-3 w-3 ${n <= fb.rating! ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                        <p className="text-sm mt-2">{fb.content}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          User: {fb.userId.substring(0, 8)}... &bull; {formatTimestamp(fb.createdAt)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {fb.status === 'new' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReviewFeedback(fb.id!, 'reviewed')}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReviewFeedback(fb.id!, 'actioned')}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Badge className={getFeedbackStatusColor(fb.status)}>
                          {fb.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feature Requests Tab */}
      {activeTab === 'features' && (
        <Card>
          <CardHeader>
            <CardTitle>Feature Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {featureRequests.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No feature requests</p>
              ) : (
                featureRequests.map(fr => (
                  <div key={fr.id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium">{fr.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{fr.description}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <Badge variant="outline">{fr.category}</Badge>
                          <div className="flex items-center gap-1 text-sm">
                            <ThumbsUp className="h-4 w-4 text-gray-400" />
                            <span>{fr.votes} votes</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        <select
                          value={fr.status}
                          onChange={(e) => handleFeatureStatus(fr.id!, e.target.value as FeatureRequest['status'])}
                          className="text-xs border rounded px-2 py-1"
                        >
                          {FEATURE_STATUSES.map(s => (
                            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bug Reports Tab */}
      {activeTab === 'bugs' && (
        <Card>
          <CardHeader>
            <CardTitle>Bug Reports</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {bugReports.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No bug reports</p>
              ) : (
                bugReports.map(bug => (
                  <div key={bug.id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{bug.title}</h4>
                          <Badge className={getBugSeverityColor(bug.severity)}>
                            {bug.severity}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{bug.description}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          {bug.deviceInfo.platform} {bug.deviceInfo.version} &bull; {formatTimestamp(bug.createdAt)}
                        </p>
                      </div>
                      <select
                        value={bug.status}
                        onChange={(e) => handleBugStatus(bug.id!, e.target.value as BugReport['status'])}
                        className="text-xs border rounded px-2 py-1"
                      >
                        {BUG_STATUSES.map(s => (
                          <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Macros Tab */}
      {activeTab === 'macros' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Support Macros</CardTitle>
                <Button
                  size="sm"
                  onClick={() => setShowCreateMacro(!showCreateMacro)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create Macro
                </Button>
              </div>
              <CardDescription>
                Reusable actions for common support workflows
              </CardDescription>
            </CardHeader>
            <CardContent>
              {showCreateMacro && (
                <div className="p-4 border rounded-lg mb-6 bg-gray-50 space-y-4">
                  <h4 className="font-medium text-sm">New Macro</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Name</label>
                      <Input
                        value={newMacroName}
                        onChange={(e) => setNewMacroName(e.target.value)}
                        placeholder="e.g. Close with thanks"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Category</label>
                      <select
                        value={newMacroCategory}
                        onChange={(e) => setNewMacroCategory(e.target.value)}
                        className="w-full border rounded px-2 py-2 text-sm"
                      >
                        <option value="general">General</option>
                        <option value="billing">Billing</option>
                        <option value="technical">Technical</option>
                        <option value="onboarding">Onboarding</option>
                        <option value="closing">Closing</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Description</label>
                    <Input
                      value={newMacroDescription}
                      onChange={(e) => setNewMacroDescription(e.target.value)}
                      placeholder="What does this macro do?"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 block mb-2">Actions ({newMacroActions.length})</label>
                    {newMacroActions.length > 0 && (
                      <div className="space-y-1 mb-3">
                        {newMacroActions.map((action, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                            <div>
                              <Badge variant="outline" className="mr-2">{action.type.replace(/_/g, ' ')}</Badge>
                              <span className="text-gray-600 text-xs">
                                {Object.values(action.params)[0]}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setNewMacroActions(prev => prev.filter((_, j) => j !== i))}
                            >
                              <Trash2 className="h-3 w-3 text-red-400" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <select
                        value={newMacroActionType}
                        onChange={(e) => setNewMacroActionType(e.target.value as MacroAction['type'])}
                        className="border rounded px-2 py-1 text-sm"
                      >
                        <option value="send_message">Send Message</option>
                        <option value="change_status">Change Status</option>
                        <option value="add_tag">Add Tag</option>
                      </select>
                      <Input
                        value={newMacroActionParam}
                        onChange={(e) => setNewMacroActionParam(e.target.value)}
                        placeholder={
                          newMacroActionType === 'send_message' ? 'Message text...' :
                          newMacroActionType === 'change_status' ? 'resolved, closed, etc.' :
                          'Tag name...'
                        }
                        className="flex-1 text-sm"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddMacroAction(); }}
                      />
                      <Button size="sm" variant="outline" onClick={handleAddMacroAction}>
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => {
                      setShowCreateMacro(false);
                      setNewMacroName('');
                      setNewMacroDescription('');
                      setNewMacroActions([]);
                    }}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateMacro} disabled={!newMacroName.trim() || newMacroActions.length === 0}>
                      Create Macro
                    </Button>
                  </div>
                </div>
              )}

              {macros.length === 0 && !showCreateMacro ? (
                <div className="text-center py-8">
                  <Settings className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No macros created yet</p>
                  <p className="text-sm text-gray-400 mt-1">Create macros to speed up common support tasks</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {macros.map(macro => (
                    <div key={macro.id} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-yellow-500" />
                            <h4 className="font-medium">{macro.name}</h4>
                            <Badge variant="outline" className="text-xs">{macro.category}</Badge>
                          </div>
                          {macro.description && (
                            <p className="text-sm text-gray-500 mt-1">{macro.description}</p>
                          )}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {macro.actions.map((action, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {action.type.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="text-right text-xs text-gray-400">
                          <p>Used {macro.usageCount} times</p>
                          <p className="mt-1">{formatTimestamp(macro.createdAt)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* User Lookup Tab */}
      {activeTab === 'lookup' && (
        <Card>
          <CardHeader>
            <CardTitle>User Lookup</CardTitle>
            <CardDescription>Search for a user to view their support history</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-6">
              <Input
                value={lookupUserId}
                onChange={(e) => setLookupUserId(e.target.value)}
                placeholder="Enter user ID or email..."
                className="max-w-md"
              />
              <Button onClick={handleUserLookup}>
                <Search className="h-4 w-4 mr-2" />
                Lookup
              </Button>
            </div>

            {lookupResult && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">{lookupResult.user.displayName || 'Unknown'}</p>
                      <p className="text-sm text-gray-500">{lookupResult.user.email}</p>
                      <p className="text-xs text-gray-400">ID: {lookupResult.user.id}</p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">Tickets ({lookupResult.tickets.length})</h4>
                    <div className="space-y-2">
                      {lookupResult.tickets.slice(0, 5).map((t: SupportTicket) => (
                        <div key={t.id} className="p-2 bg-gray-50 rounded text-sm">
                          <p className="font-medium">{t.subject}</p>
                          <Badge className={getStatusColor(t.status)}>{t.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Feedback ({lookupResult.feedback.length})</h4>
                    <div className="space-y-2">
                      {lookupResult.feedback.slice(0, 5).map((f: UserFeedback) => (
                        <div key={f.id} className="p-2 bg-gray-50 rounded text-sm">
                          <p className="line-clamp-2">{f.content}</p>
                          <Badge variant="outline">{f.type}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
